/** Provider-configured sales tax rates (Jobber-style; provider remits). */

import { getStateFromZip } from './zipToState';

export type SalesTaxPropertyClass = 'residential' | 'commercial';

export interface ProviderSalesTaxRate {
  state: string;
  residentialPercent: number;
  commercialPercent: number;
}

export function normalizeSalesTaxRates(
  rates: Array<Partial<ProviderSalesTaxRate>> | null | undefined,
): ProviderSalesTaxRate[] {
  if (!Array.isArray(rates)) return [];
  const byState = new Map<string, ProviderSalesTaxRate>();
  for (const r of rates) {
    const state = String(r?.state || '')
      .trim()
      .toUpperCase()
      .slice(0, 2);
    if (!/^[A-Z]{2}$/.test(state)) continue;
    const residentialPercent = clampPercent(r?.residentialPercent);
    const commercialPercent = clampPercent(r?.commercialPercent);
    byState.set(state, { state, residentialPercent, commercialPercent });
  }
  return Array.from(byState.values()).sort((a, b) => a.state.localeCompare(b.state));
}

function clampPercent(n: unknown): number {
  const v = typeof n === 'number' ? n : parseFloat(String(n ?? ''));
  if (!Number.isFinite(v) || v < 0) return 0;
  if (v > 20) return 20;
  return Math.round(v * 1000) / 1000;
}

export function propertyTypeToTaxClass(
  propertyType?: string | null,
): SalesTaxPropertyClass {
  if (propertyType === 'commercial') return 'commercial';
  return 'residential';
}

export function resolveSalesTaxPercent(
  rates: ProviderSalesTaxRate[] | null | undefined,
  stateCode: string | null | undefined,
  propertyType?: string | null,
): { percent: number; state: string | null; propertyClass: SalesTaxPropertyClass } {
  const propertyClass = propertyTypeToTaxClass(propertyType);
  const state = String(stateCode || '')
    .trim()
    .toUpperCase()
    .slice(0, 2);
  if (!/^[A-Z]{2}$/.test(state) || !Array.isArray(rates)) {
    return { percent: 0, state: state || null, propertyClass };
  }
  const row = rates.find((r) => r.state === state);
  if (!row) return { percent: 0, state, propertyClass };
  const percent =
    propertyClass === 'commercial' ? row.commercialPercent : row.residentialPercent;
  return { percent: clampPercent(percent), state, propertyClass };
}

export function mergeSalesTaxRatesForStates(
  existing: ProviderSalesTaxRate[] | null | undefined,
  states: string[],
): ProviderSalesTaxRate[] {
  const prev = normalizeSalesTaxRates(existing);
  const byState = new Map(prev.map((r) => [r.state, r]));
  return states.map((state) => {
    const code = state.toUpperCase();
    const found = byState.get(code);
    return (
      found || {
        state: code,
        residentialPercent: 0,
        commercialPercent: 0,
      }
    );
  });
}

/** Resolve checkout tax options from provider rates + job address / property type. */
export function salesTaxOptionsForJob(
  rates: ProviderSalesTaxRate[] | null | undefined,
  address?: { state?: string; zipCode?: string } | null,
  propertyType?: string | null,
): { salesTaxPercent: number; salesTaxState: string | null } {
  const state =
    (address?.state && String(address.state).trim()) ||
    getStateFromZip(address?.zipCode || '') ||
    null;
  const resolved = resolveSalesTaxPercent(rates, state, propertyType);
  return { salesTaxPercent: resolved.percent, salesTaxState: resolved.state };
}
